// src/pages/Payment.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom"; // Import useNavigate if needed for navigation
import axios from "axios";
import { CreditCard, AlertTriangle, ListChecks, XCircle, CheckCircle, Info } from 'lucide-react'; // Using Lucide icons

// Components
// import Sidebar from "../components/Sidebar"; // REMOVE
import Footerbar from "../components/Footerbar"; // ADD
import Navbar from "../components/Navbar"; // ADD

// --- Helper: Inject CSS Keyframes for Spinners ---
if (!document.getElementById('spinner-keyframes')) {
    const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.id = 'spinner-keyframes';
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
}
// --- End Helper ---


const Payment = () => {
    const [payments, setPayments] = useState([]);
    const [selectedPayment, setSelectedPayment] = useState(null); // For modal
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // Add error state
    // const [isSidebarOpen, setIsSidebarOpen] = useState(false); // REMOVE
    const navigate = useNavigate(); // Initialize useNavigate if needed

    // --- Fetch Payment History ---
    useEffect(() => {
        let isMounted = true;
        const fetchPayments = async () => {
            setLoading(true);
            setError(null);
            setPayments([]); // Clear previous payments
            console.log("PaymentScreen: Fetching payment data...");

            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    if (isMounted) navigate('/'); // Redirect if no token
                    return;
                }

                const response = await axios.get("https://apem.riderup.uk/api/listPayments", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (isMounted) {
                    console.log("PaymentScreen: Payments received:", response.data?.length);
                    // Sort payments by date, newest first
                    const sortedPayments = response.data?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) || [];
                    setPayments(sortedPayments);
                }
            } catch (error) {
                console.error("PaymentScreen: Erro ao buscar pagamentos:", error);
                if (isMounted) {
                    if (axios.isAxiosError(error) && error.response?.status === 401) {
                        localStorage.removeItem("token");
                        navigate('/'); // Redirect on 401
                    } else {
                        setError(error.message || "Não foi possível carregar os pagamentos.");
                    }
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        fetchPayments();
        return () => { isMounted = false; } // Cleanup
    }, [navigate]); // Add navigate to dependencies

    // --- Helper Functions ---
    const formatCurrency = (amount) => {
        const numberAmount = Number(amount);
        if (isNaN(numberAmount)) {
            return "Valor inválido";
        }
        return numberAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Data indisponível';
        try {
            const date = new Date(dateString);
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'Data inválida';
            }
            return date.toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch (e) {
            return 'Data inválida';
        }
    };

    const getStatusInfo = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
            case 'succeeded': // Handle stripe status
                return { text: 'Aprovado', color: '#50C878', icon: <CheckCircle size={14} /> };
            case 'pending':
            case 'in_process':
                return { text: 'Pendente', color: '#FFD700', icon: <Info size={14} /> }; // Yellow/Gold
            case 'rejected':
            case 'cancelled':
            case 'failed': // Handle stripe status
                return { text: 'Recusado/Cancelado', color: '#ff6060', icon: <XCircle size={14} /> }; // Red
            default:
                return { text: status || 'Desconhecido', color: '#a0a0b0', icon: <Info size={14} /> }; // Gray
        }
    };


    // --- Render ---
    return (
        <div style={styles.pageContainer}>
            {/* Remove openSidebar prop */}
            <Navbar title="Meus Pagamentos" showLogo={false} />

            {/* Content Area */}
            <div style={styles.contentArea}>
                {loading ? (
                    <div style={styles.centeredMessage}>
                        <div style={styles.spinner}></div>
                        <p>Carregando pagamentos...</p>
                    </div>
                ) : error ? (
                    <div style={styles.centeredMessage}>
                        <AlertTriangle size={40} color="#ff6060" />
                        <p style={styles.errorText}>{error}</p>
                        {/* Optional: Add retry button */}
                    </div>
                ) : payments.length === 0 ? (
                    <div style={styles.centeredMessage}>
                        <ListChecks size={40} color="#a0a0b0" />
                        <p style={{ color: "#a0a0b0", marginTop: '10px' }}>Você ainda não realizou pagamentos.</p>
                    </div>
                ) : (
                    <div style={styles.listContainer}>
                        {payments.map((payment) => {
                            const statusInfo = getStatusInfo(payment.status);
                            return (
                                <div
                                    key={payment.id}
                                    onClick={() => setSelectedPayment(payment)}
                                    style={styles.paymentCard}
                                >
                                    <CreditCard size={28} color={styles.cardIcon.color} style={styles.cardIcon} />
                                    <div style={styles.itemInfo}>
                                        <h3 style={styles.itemDescription}>{payment.description || 'Compra de Créditos'}</h3>
                                        <p style={styles.itemAmount}>{formatCurrency(payment.transaction_amount)}</p>
                                        <p style={{ ...styles.itemStatus, color: statusInfo.color }}>
                                            {statusInfo.icon}
                                            <span style={{ marginLeft: '5px' }}>{statusInfo.text}</span>
                                        </p>
                                    </div>
                                    {/* Optional: Add arrow or indicator for clickability */}
                                    {/* <ChevronRight size={20} color="#a0a0b0" /> */}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div> {/* End Content Area */}

            {/* Payment Details Modal */}
            {selectedPayment && (
                <div
                    style={styles.modalOverlay}
                    onClick={() => setSelectedPayment(null)} // Close on overlay click
                >
                    {/* Prevent closing when clicking inside modal content */}
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Detalhes do Pagamento</h3>
                        <div style={styles.modalDetailItem}>
                            <strong>ID Transação:</strong>
                            <span>{selectedPayment.payment_id || 'N/A'}</span>
                        </div>
                        <div style={styles.modalDetailItem}>
                            <strong>Valor:</strong>
                            <span>{formatCurrency(selectedPayment.transaction_amount)}</span>
                        </div>
                        <div style={styles.modalDetailItem}>
                            <strong>Método:</strong>
                            <span>{selectedPayment.payment_method || 'Não informado'}</span>
                        </div>
                        <div style={styles.modalDetailItem}>
                            <strong>Status:</strong>
                            <span style={{ color: getStatusInfo(selectedPayment.status).color }}>
                                {getStatusInfo(selectedPayment.status).text}
                            </span>
                        </div>
                        <div style={styles.modalDetailItem}>
                            <strong>Descrição:</strong>
                            <span>{selectedPayment.description || 'Compra de Créditos'}</span>
                        </div>
                        <div style={styles.modalDetailItem}>
                            <strong>Data:</strong>
                            <span>{formatDate(selectedPayment.created_at)}</span>
                        </div>

                        <button
                            style={styles.modalCloseButton}
                            onClick={() => setSelectedPayment(null)}
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}

            <Footerbar />
        </div> // End Page Container
    );
};


// --- Styles ---
const styles = {
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#12121f',
        color: '#f0f0f0',
        overflowX: 'hidden',
        boxSizing: 'border-box',
    },
    contentArea: {
        flexGrow: 1,
        paddingTop: '70px', // Space below Navbar
        paddingBottom: '75px', // Space above Footerbar
        paddingLeft: '15px', // Adjusted padding
        paddingRight: '15px', // Adjusted padding
        overflowY: 'auto',
        boxSizing: 'border-box',
        width: '100%',
    },
    centeredMessage: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 20px',
        color: '#a0a0b0',
        minHeight: 'calc(80vh - 145px)', // Adjust height calculation
        boxSizing: 'border-box',
    },
    errorText: {
        color: '#ff6060',
        marginTop: '10px',
        maxWidth: '80%',
        lineHeight: '1.4',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
    },
    spinner: {
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#a0a0ff',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        animation: 'spin 1s linear infinite',
        marginBottom: '15px',
    },
    listContainer: {
        width: '100%',
        maxWidth: '600px', // Max width for the list
        marginLeft: 'auto',
        marginRight: 'auto',
        display: "grid", // Use grid or flex column
        gap: "15px",
    },
    paymentCard: {
        display: "flex",
        alignItems: "center",
        backgroundColor: "#1a1a2e", // Dark card background
        padding: "15px",
        borderRadius: "10px",
        cursor: "pointer",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.4)", // Darker shadow
        border: '1px solid #2a2a4e', // Subtle border
        transition: 'background-color 0.2s ease',
        // ':hover': { backgroundColor: '#2a2a4e' } // Requires CSS modules/libs
    },
    cardIcon: {
        marginRight: "15px",
        color: "#a0a0ff", // Theme purple color for icon
        flexShrink: 0,
    },
    itemInfo: {
        flex: 1, // Take remaining space
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0, // Allow shrinking
    },
    itemDescription: {
        margin: '0 0 4px 0',
        fontSize: '15px',
        fontWeight: '600',
        color: '#ffffff', // White text
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    itemAmount: {
        margin: '0 0 4px 0',
        color: "#c0c0ff", // Lighter purple for amount
        fontSize: '14px',
        fontWeight: '500',
    },
    itemStatus: {
        margin: 0,
        fontSize: "12px",
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        // Color is set dynamically based on status
    },

    // --- Modal Styles ---
    modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.8)", // Darker overlay
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1100, // Ensure modal is above navbar/footer
        padding: '20px',
        boxSizing: 'border-box',
    },
    modalContent: {
        backgroundColor: "#1a1a2e", // Dark modal background
        color: '#f0f0f0',
        padding: "25px 30px", // Adjusted padding
        borderRadius: "12px",
        width: "100%", // Full width within overlay padding
        maxWidth: "450px", // Max width
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.5)",
        maxHeight: '85vh', // Max height
        overflowY: 'auto', // Allow scroll if content exceeds height
        boxSizing: 'border-box',
    },
    modalTitle: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#d0d0ff', // Light purple title
        marginBottom: '20px',
        textAlign: 'center',
        borderBottom: '1px solid #3a3a5e', // Separator line
        paddingBottom: '10px',
    },
    modalDetailItem: {
        display: 'flex',
        justifyContent: 'space-between', // Align label and value
        alignItems: 'center',
        marginBottom: '12px',
        paddingBottom: '8px', // Space below each item
        borderBottom: '1px solid #2a2a4e', // Subtle separator
        fontSize: '14px',
        '& strong': { // Targeting strong tag (requires CSS/libs or separate style)
            color: '#a0a0b0', // Lighter gray for label
            marginRight: '10px',
        },
        '& span': { // Targeting value span
            textAlign: 'right',
            wordBreak: 'break-word', // Allow long values to wrap
        },
        // Apply directly using inline style for now for simplicity
        // Example in JSX: <p><strong style={styles.modalLabel}>ID:</strong> <span style={styles.modalValue}>{...}</span></p>
    },
    // Simpler inline approach for modalDetailItem (less clean but works):
    // <p style={{ fontSize: '14px', marginBottom: '10px' }}>
    //    <strong style={{ color: '#a0a0b0', marginRight: '5px' }}>ID:</strong>
    //    <span style={{ wordBreak: 'break-all' }}>{selectedPayment.payment_id || 'N/A'}</span>
    // </p>

    modalCloseButton: {
        width: "100%",
        padding: "12px",
        backgroundColor: "#6a0dad", // Theme purple
        color: "#fff",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        marginTop: "20px", // Space above close button
        fontSize: '16px',
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease',
        // ':hover': { backgroundColor: '#5a0ca0' } // Requires CSS modules/libs
    },
}; // --- End Styles ---
export default Payment;